export function sendRequest({
    endpoint,
    params,
    body,
    config,
    responseInterceptor,
    errorInterceptor,
}) {
    const url = getUrl(endpoint, params);
    const settings = {
        method: endpoint.method,
        body,
        ...config,
    };
    const request = {
        url,
        settings,
        calledFromErrorInterceptor: false,
    };

    return callFetch(request, responseInterceptor, errorInterceptor);
}

export function callFetch(request, responseInterceptor, errorInterceptor) {
    const responseFn = responseInterceptor.bind(null, request);
    const errorFn = errorInterceptor.bind(null, request);

    return fetch(request.url, request.settings)
        .then(responseFn)
        .catch(errorFn);
}

/**
 * The parsed JSON body of a response, however the interceptor left it.
 *
 * `responseInterceptor` parses the body into `response.data` only when the response carries a
 * non-zero `Content-Length`. A chunked or compressed response legitimately omits that header, and
 * then `data` is simply absent while the body itself is still unread — which reads as an empty
 * response to any caller that trusts `data` alone.
 *
 * That went unnoticed while nothing read a body on the SSO path: the console flow takes its session
 * from cookies auth-service sets on the exchange response, so an unparsed body cost nothing. The
 * widget path reads the token pair out of it, so it has to handle both shapes.
 *
 * @param {Response} response a resolved fetch response
 * @returns {Promise<Object|null>} the parsed body, or null when there is nothing to parse
 */
export function readJsonBody(response) {
    if (response.data) {
        return Promise.resolve(response.data);
    }
    if (response.bodyUsed) {
        return Promise.resolve(null);
    }
    return response.json().catch(() => null);
}

export function getUrl(endpoint, params) {
    const paramsClone = { ...params };
    let url = endpoint.domain + endpoint.path;
    url = processRouteParams(url, paramsClone);
    url = processQueryParams(url, paramsClone, endpoint.queryParams);

    return url;
}

export function processRouteParams(url, params) {
    const ROUTE_PARAM_PATTERN = /\{\w{0,}\}/g;

    const routeParams = url.match(ROUTE_PARAM_PATTERN);
    if (routeParams) {
        for (const param of routeParams) {
            const paramName = param.slice(1, -1);
            const paramValue = params[paramName];
            url = url.replace(param, paramValue);

            delete params[paramName];
        }
    }
    return url;
}

export function processQueryParams(url, params, allowedParams) {
    if (allowedParams) {
        const query = new URLSearchParams();
        for (const paramName in params) {
            const paramValue = params[paramName];
            const isAllowed = allowedParams.includes(paramName);
            if (isAllowed) {
                query.set(paramName, paramValue);
            }
        }
        const queryString = query.toString();
        if (queryString) {
            return `${url}?${queryString}`;
        }
    }
    return url;
}

export function getEndpoint(domain, path, method, queryParams) {
    return {
        domain,
        path,
        method,
        queryParams,
    };
}
